/**
 * INTEGRITY SYSTEM - Sistema de Integridad
 * Maneja la salud de la democracia (0-100%)
 */

import { ROUND_DEFAULTS } from "../utils/constants.js";
import { clamp } from "../utils/helpers.js";

export default class IntegritySystem {
  constructor(scene) {
    this.scene = scene;
    this.current = ROUND_DEFAULTS.INITIAL_INTEGRITY;
    this.max = 100;
    this.min = 0;
  }

  /**
   * Obtener integridad actual
   * @returns {number}
   */
  getCurrent() {
    return this.current;
  }

  /**
   * Establecer integridad
   * @param {number} value
   */
  set(value) {
    this.current = clamp(value, this.min, this.max);
    this.onIntegrityChanged();
  }

  /**
   * Agregar integridad
   * @param {number} amount
   * @returns {number} Nueva integridad
   */
  add(amount) {
    // Validar que amount es un número válido
    if (isNaN(amount) || amount === null || amount === undefined) {
      console.warn("IntegritySystem.add: amount inválido", amount);
      return this.current;
    }

    this.current = clamp(this.current + amount, this.min, this.max);
    this.onIntegrityChanged();
    return this.current;
  }

  /**
   * Restar integridad
   * @param {number} amount
   * @returns {number} Nueva integridad
   */
  subtract(amount) {
    // Validar que amount es un número válido
    if (isNaN(amount) || amount === null || amount === undefined) {
      console.warn("IntegritySystem.subtract: amount inválido", amount);
      return this.current;
    }

    this.current = clamp(this.current - amount, this.min, this.max);
    this.onIntegrityChanged();
    return this.current;
  }

  /**
   * Verificar si está por encima del umbral de victoria
   * @param {number} threshold - Umbral (default: 70%)
   * @returns {boolean}
   */
  isAboveThreshold(threshold = ROUND_DEFAULTS.WIN_THRESHOLD) {
    return this.current >= threshold;
  }

  /**
   * Verificar si está en peligro
   * @param {number} dangerThreshold - Umbral de peligro (default: 40%)
   * @returns {boolean}
   */
  isInDanger(dangerThreshold = 40) {
    return this.current < dangerThreshold;
  }

  /**
   * Verificar si la integridad está crítica
   * @param {number} criticalThreshold - Umbral crítico (default: 20%)
   * @returns {boolean}
   */
  isCritical(criticalThreshold = 20) {
    return this.current < criticalThreshold;
  }

  /**
   * Obtener porcentaje como string
   * @returns {string}
   */
  getPercentage() {
    return `${Math.round(this.current)}%`;
  }

  /**
   * Obtener nivel (high, medium, low)
   * @returns {string}
   */
  getLevel() {
    if (this.current > 70) return "high";
    if (this.current >= 40) return "medium";
    return "low";
  }

  /**
   * Resetear a valor inicial
   * @param {number} value - Valor inicial (opcional)
   */
  reset(value = ROUND_DEFAULTS.INITIAL_INTEGRITY) {
    this.current = value;
    this.onIntegrityChanged();
  }

  /**
   * Callback cuando cambia la integridad
   * Se puede sobrescribir para actualizar UI
   */
  onIntegrityChanged() {
    // Emit event para que otros sistemas reaccionen
    this.scene.events.emit("integrityChanged", this.current);
  }

  /**
   * Destruir el sistema
   */
  destroy() {
    this.scene = null;
  }
}
